import { ReactElement } from 'react'
import { GetServerSideProps, InferGetServerSidePropsType, Redirect } from 'next'
import {
  dehydrate,
  DehydratedState,
  IEntriesProps
} from '@smuchile/pkg-unimarc-hooks'
import { ErrorPageV2Props, ProductOfferPage } from 'components'
import { getContentOffers } from 'shared/utils/offers/getContentOffers'
import { MetaHead } from '../../components'
import { useRouter } from 'next/router'
import {
  cleanPageNumber,
  cleanPageNumberParam,
  getHeaderCookies
} from '../../shared/helpers'
import { ICustomGetServerSidePropsResult } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/ICustomGetServerSidePropsResult'
import dynamic from 'next/dynamic'
import { LayoutAndErrorBundary } from 'components/LayoutAndErrorBundary'
import CurtainWrapper from 'components/CurtainWrapper'

const ErrorPage = dynamic<ErrorPageV2Props>(
  import('components').then((mod) => {
    return mod.ErrorPageV2
  })
)

export interface OffersProps {
  bannerLegal?: IEntriesProps
  dehydratedState?: DehydratedState
  description?: string
  error?: boolean
  isMobile?: boolean
  page?: number
  reference?: string
  robotTag?: string
  title?: string
  clusterId?: string
  clusterName?: string
  statusCode?: number
}

const Offers = ({
  bannerLegal,
  clusterId,
  clusterName,
  description,
  isMobile,
  page,
  reference,
  robotTag,
  title,
  statusCode
}: InferGetServerSidePropsType<
  typeof getServerSideProps
>): React.ReactElement => {
  const router = useRouter()

  if (statusCode === 500) {
    return <ErrorPage errorType='500' />
  }

  const categories = router?.query?.offers as Array<string>

  return (
    <>
      <MetaHead
        canonicalPath={cleanPageNumber(router?.asPath)}
        categories={cleanPageNumberParam(categories)}
        contentDescription={description}
        metaRobot={robotTag}
        pageNumber={`${page ?? 1}`}
        titleName={title}
        type='COLLECTION'
      />
      <ProductOfferPage
        bannerLegal={bannerLegal}
        clusterId={clusterId}
        clusterName={clusterName}
        isMobile={isMobile}
        key={clusterId || clusterName}
        page={page}
        reference={reference}
        title={title}
      />
    </>
  )
}

Offers.getLayout = (page: ReactElement) => {
  return (
    <CurtainWrapper>
      <LayoutAndErrorBundary>{page}</LayoutAndErrorBundary>
    </CurtainWrapper>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context
): Promise<ICustomGetServerSidePropsResult<OffersProps, Redirect>> => {
  const currentPage = context?.query?.['page']
  const parseCurrentPage = currentPage ? Number(currentPage) : 1
  let contentFulUrl = context?.resolvedUrl?.split('?')[0]
  const headers = getHeaderCookies(context)

  const segments = contentFulUrl?.split('/')
  contentFulUrl = segments && `/${segments[1]}/${segments[2]}`

  // helper to cache chips and collections web in query cache and get offers products
  const {
    bannerLegal,
    clusterId,
    clusterName,
    description,
    error,
    queryClient,
    reference,
    robotTag,
    title,
    intelliSearchKeyName
  } = await getContentOffers({
    contenType: 'coleccionesWeb',
    headers,
    idFormat: 1,
    currentPage: parseCurrentPage,
    contentFulUrl
  })

  const isErrorintelliSearch =
    queryClient.getQueryData(intelliSearchKeyName)?.['error']

  // Error 500
  if (isErrorintelliSearch) {
    return {
      props: {
        statusCode: 500
      }
    }
  }

  // Error 404
  if (error) {
    return {
      props: null,
      notFound: true
    }
  }

  return {
    props: {
      bannerLegal,
      clusterId,
      clusterName,
      description,
      dehydratedState: dehydrate(queryClient),
      error,
      page: parseCurrentPage || null,
      reference: reference,
      robotTag,
      title,
      statusCode: null
    }
  }
}

export default Offers
